import {
  type PlanBuildData,
  type PlanConfig,
  type PlanStop,
  type StopBuilder,
  ITEMS_PER_MINUTE,
} from "../plan-template.js";
import { currentRetrievability } from "../fsrs.js";

/**
 * Maintenance fallback — fires only when every other stop returned null
 * (alreadyPlanned is empty). Surfaces the N items with the lowest current
 * retrievability, regardless of whether they are technically "due" yet.
 *
 * Fires in all sessionMix modes, including "review-only", since maintenance
 * is itself a form of review. FSRS grading runs normally; reviewing slightly
 * ahead of schedule is handled correctly by the stability update equations.
 *
 * Items must still pass Filters A/B/C — the prerequisite system is respected.
 */
export const maintenanceStop: StopBuilder = (
  data: PlanBuildData,
  config: PlanConfig,
  alreadyPlanned: ReadonlySet<string>,
): PlanStop | null => {
  // Only fire when all primary stops produced nothing.
  if (alreadyPlanned.size > 0) return null;

  const { items, cards, nodes, now } = data;
  const { maintenanceItemCap } = config;

  const candidates = items
    .filter(
      (i) =>
        data.unlockedNodeSet.has(i.nodeId) &&
        data.engagedNodeSet.has(i.nodeId) &&
        data.practiceReviewedNodeSet.has(i.nodeId) &&
        !alreadyPlanned.has(i.id) &&
        cards[i.id] !== undefined &&
        cards[i.id]!.state !== "new",
    )
    .map((i) => ({ item: i, r: currentRetrievability(cards[i.id]!, now) }))
    .sort((a, b) => a.r - b.r) // weakest first
    .slice(0, maintenanceItemCap)
    .map((x) => x.item);

  if (candidates.length === 0) return null;

  const nodeCount = new Map<string, number>();
  for (const item of candidates) {
    nodeCount.set(item.nodeId, (nodeCount.get(item.nodeId) ?? 0) + 1);
  }
  const topTitles = [...nodeCount.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([nodeId]) => nodes.find((n) => n.id === nodeId)?.title ?? nodeId);

  return {
    kind: "maintenance",
    title: "Keep sharp",
    reason: `${topTitles.join(" · ")} — nothing due today, but these are your weakest items right now.`,
    itemCount: candidates.length,
    estimatedMinutes: Math.max(1, Math.round(candidates.length / ITEMS_PER_MINUTE)),
    items: candidates,
  };
};
